import { Config } from '../Config';

const sessionCache = new Map();

function cacheKey(params) {
  return JSON.stringify({
    s: params.source_page || '',
    us: params.utm_source || '',
    um: params.utm_medium || '',
    uc: params.utm_campaign || '',
    p: params.partner_id || '',
    rc: params.referral_code || '',
    tp: params.target_path || '',
  });
}

function detectLocale() {
  return (navigator.language || navigator.userLanguage || '').slice(0, 8) || null;
}

function detectTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    return null;
  }
}

function detectScreen() {
  if (typeof window === 'undefined') return null;
  return window.innerWidth + 'x' + window.innerHeight;
}

/**
 * Read user tracking consent in priority order:
 *   1. window.__trackingConsent  (set by any future cookie banner integration)
 *   2. tracking_consent cookie    (set by GTM consent mode hook or banner)
 *   3. window.dataLayer GTM consent_default/update events (analytics_storage)
 * Returns one of: 'authorized', 'denied', 'unavailable', or null if no
 * signal is present (backend will normalize null → 'unavailable').
 */
function detectTrackingConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const g = window.__trackingConsent;
    if (g === 'authorized' || g === 'granted' || g === true) return 'authorized';
    if (g === 'denied' || g === false) return 'denied';
  } catch (_) {}
  try {
    const m = document.cookie.match(/(?:^|; )tracking_consent=([^;]+)/);
    if (m) {
      const v = decodeURIComponent(m[1]).toLowerCase();
      if (v === 'authorized' || v === 'granted') return 'authorized';
      if (v === 'denied') return 'denied';
    }
  } catch (_) {}
  try {
    const dl = window.dataLayer;
    if (Array.isArray(dl)) {
      for (let i = dl.length - 1; i >= 0; i--) {
        const e = dl[i];
        if (e && e[0] === 'consent' && e[2] && typeof e[2].analytics_storage === 'string') {
          return e[2].analytics_storage === 'granted' ? 'authorized' : 'denied';
        }
      }
    }
  } catch (_) {}
  return null;
}

/**
 * Create (or retrieve cached) smart link for an "install the app" click.
 *
 * Returns: Promise<{ click_id, smart_link, fallback_used }>.
 * On backend failure, smart_link comes back null and the caller is
 * expected to fall back to the platform-specific store URL so the CTA
 * never dead-ends (attribution is lost for that click).
 */
export async function createSmartLink(params = {}) {
  const key = cacheKey(params);
  if (sessionCache.has(key)) {
    return sessionCache.get(key);
  }

  const body = {
    source_page: params.source_page || 'unknown',
    utm_source: params.utm_source || null,
    utm_medium: params.utm_medium || null,
    utm_campaign: params.utm_campaign || null,
    utm_term: params.utm_term || null,
    utm_content: params.utm_content || null,
    partner_id: params.partner_id || null,
    referral_code: params.referral_code || null,
    target_path: params.target_path || null,
    locale: detectLocale(),
    tz: detectTz(),
    screen: detectScreen(),
    tracking_consent: detectTrackingConsent(),
  };

  const url = Config.hostUrl + 'links/click';

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      throw new Error('HTTP ' + resp.status);
    }
    const data = await resp.json();
    if (!data || !data.smart_link) {
      throw new Error('no smart_link in response');
    }
    const result = {
      click_id: data.click_id,
      smart_link: data.smart_link,
      fallback_used: false,
    };
    sessionCache.set(key, result);
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('appLink.createSmartLink failed, using fallback:', err && err.message);
    return {
      click_id: null,
      smart_link: null,
      fallback_used: true,
    };
  }
}

/**
 * Fire a downstream click event into the existing GTM/Meta Pixel pipeline.
 * No new tags — piggybacks on dataLayer + fbq if present.
 */
export function reportClickEvent(eventName, payload) {
  try {
    if (typeof window !== 'undefined' && window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({ event: eventName, ...payload });
    }
  } catch (_) {}
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('trackCustom', eventName, payload);
    }
  } catch (_) {}
}



// WEBPACK FOOTER //
// ./src/libs/appLink.js